import { Component, Show, createEffect, createMemo, createSignal } from "solid-js"
import defaultScenario from "../data/scenario/default-scenario.json"
import emptyScenario from "../data/scenario/empty-scenario.json"
import christerDelta from "../data/scenario/scenario_christer_delta.json"
import { mergeScenarios } from "../lib/scenario-merge"
import { sanitizeScenarioFile, validateScenarioFile } from "../lib/scenario-validator"
import type { ScenarioMeta, SimTime } from "../types"

export interface ScenarioConfig {
    type: "template" | "clone-current" | "from-file"
    templateData?: any // Full scenario JSON for "template" type
    editable: boolean
    scenarioName?: string
    globalClockStart: SimTime
    crimeTime: SimTime
    soundEffectsEnabled: boolean
    fileData?: any
}

interface ScenarioSelectionModalProps {
    isOpen: boolean
    onClose: () => void
    onSelect: (config: ScenarioConfig) => void
    currentMeta: ScenarioMeta
}

// All bundled templates in display order.
const vastandeGM = mergeScenarios(
    defaultScenario as any,
    christerDelta as any,
    { name: "Väntande GM" }
)

const TEMPLATES = [
    { data: defaultScenario as any },
    { data: vastandeGM },
    { data: emptyScenario as any },
]

// Index of the blank "Tomt scenario" template — always forced editable
const EMPTY_TEMPLATE_IDX = TEMPLATES.length - 1

const ScenarioSelectionModal: Component<ScenarioSelectionModalProps> = (props) => {
    // Primary selection: which template card (null when secondary option is active)
    const [selectedTemplateIdx, setSelectedTemplateIdx] = createSignal<number | null>(0)
    // Secondary option overrides template selection when not null
    const [secondaryType, setSecondaryType] = createSignal<"clone-current" | "from-file" | null>(null)

    // "Redigerbar kopia" toggle (hidden + forced true for Tomt scenario)
    const [editable, setEditable] = createSignal(false)

    // Editable detail fields
    const [scenarioName, setScenarioName] = createSignal("")
    const [startHour, setStartHour] = createSignal(0)
    const [startMinute, setStartMinute] = createSignal(0)
    const [startSecond, setStartSecond] = createSignal(0)
    const [crimeHour, setCrimeHour] = createSignal(0)
    const [crimeMinute, setCrimeMinute] = createSignal(0)
    const [crimeSecond, setCrimeSecond] = createSignal(0)
    const [soundEffectsEnabled, setSoundEffectsEnabled] = createSignal(true)

    // File upload state
    const [fileData, setFileData] = createSignal<any>(null)
    const [fileError, setFileError] = createSignal("")

    // Tomt scenario is always editable
    const isEmptyTemplate = createMemo(() => selectedTemplateIdx() === EMPTY_TEMPLATE_IDX)

    const loadMetaIntoForm = (meta: any, name?: string) => {
        setScenarioName(name ?? "")
        setStartHour(meta.startTime.hour)
        setStartMinute(meta.startTime.minute)
        setStartSecond(meta.startTime.second)
        setCrimeHour(meta.crimeTime.hour)
        setCrimeMinute(meta.crimeTime.minute)
        setCrimeSecond(meta.crimeTime.second)
        setSoundEffectsEnabled(meta.soundEffects ?? true)
    }

    // When template selection changes, update form defaults
    createEffect(() => {
        const idx = selectedTemplateIdx()
        if (idx === null) return
        const meta = TEMPLATES[idx].data.meta
        loadMetaIntoForm(meta)
    })

    // When switching to clone-current, fill from current scenario meta
    createEffect(() => {
        if (secondaryType() === "clone-current") {
            const meta = props.currentMeta
            loadMetaIntoForm(meta, meta.name)
        }
    })

    const selectTemplate = (idx: number) => {
        setSelectedTemplateIdx(idx)
        setSecondaryType(null)
        if (idx === EMPTY_TEMPLATE_IDX) setEditable(true)
    }

    const selectSecondary = (type: "clone-current" | "from-file") => {
        setSecondaryType(type)
        setSelectedTemplateIdx(null)
    }

    const handleFileUpload = (event: Event) => {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]
        if (!file) return
        setFileError("")
        setFileData(null)

        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string
                const rawData = JSON.parse(text)
                const validation = validateScenarioFile(rawData)
                if (!validation.valid) {
                    throw new Error(`Ogiltig scenariofil: ${validation.errors.join(", ")}`)
                }
                const sanitized = sanitizeScenarioFile(rawData)
                setFileData(sanitized)
                setScenarioName(sanitized.meta.name || "Importerat scenario")
            } catch (err) {
                setFileError(err instanceof Error ? err.message : "Ogiltig JSON-fil")
                setFileData(null)
            }
        }
        reader.onerror = () => {
            setFileError("Kunde inte läsa filen")
            setFileData(null)
        }
        reader.readAsText(file)
    }

    const isConfirmDisabled = createMemo(() => {
        if (secondaryType() === "from-file") return !fileData()
        return false
    })

    const confirmLabel = createMemo(() => {
        if (secondaryType() === "from-file") return "Importera"
        if (secondaryType() === "clone-current") return "Skapa kopia"
        if (editable() || isEmptyTemplate()) return "Skapa kopia"
        return "Ladda"
    })

    const handleConfirm = () => {
        const sec = secondaryType()
        if (sec === "from-file") {
            if (!fileData()) return
            props.onSelect({
                type: "from-file",
                editable: true,
                globalClockStart: { hour: startHour(), minute: startMinute(), second: startSecond() },
                crimeTime: { hour: crimeHour(), minute: crimeMinute(), second: crimeSecond() },
                soundEffectsEnabled: soundEffectsEnabled(),
                fileData: fileData(),
            })
        } else if (sec === "clone-current") {
            props.onSelect({
                type: "clone-current",
                editable: true,
                scenarioName: scenarioName() || undefined,
                globalClockStart: { hour: startHour(), minute: startMinute(), second: startSecond() },
                crimeTime: { hour: crimeHour(), minute: crimeMinute(), second: crimeSecond() },
                soundEffectsEnabled: soundEffectsEnabled(),
            })
        } else {
            const idx = selectedTemplateIdx()!
            props.onSelect({
                type: "template",
                templateData: TEMPLATES[idx].data,
                editable: editable() || isEmptyTemplate(),
                scenarioName: (editable() || isEmptyTemplate()) ? (scenarioName() || undefined) : undefined,
                globalClockStart: { hour: startHour(), minute: startMinute(), second: startSecond() },
                crimeTime: { hour: crimeHour(), minute: crimeMinute(), second: crimeSecond() },
                soundEffectsEnabled: soundEffectsEnabled(),
            })
        }
        props.onClose()
        setFileData(null)
        setFileError("")
    }

    return (
        <Show when={props.isOpen}>
            <div
                class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
                onClick={props.onClose}
            >
                <div
                    class="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div class="p-6">
                        <h2 class="mb-5 text-xl font-bold text-gray-900">Nytt scenario</h2>

                        {/* Template gallery */}
                        <div class="mb-3 space-y-2">
                            {TEMPLATES.map((t, idx) => {
                                const isSelected = () => selectedTemplateIdx() === idx
                                return (
                                    <button
                                        type="button"
                                        class={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-gray-50 ${
                                            isSelected()
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-200 bg-white"
                                        }`}
                                        onClick={() => selectTemplate(idx)}
                                    >
                                        <div
                                            class={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                                isSelected() ? "border-blue-500" : "border-gray-300"
                                            }`}
                                        >
                                            <Show when={isSelected()}>
                                                <div class="h-2 w-2 rounded-full bg-blue-500" />
                                            </Show>
                                        </div>
                                        <div class="flex-1">
                                            <span class="text-sm font-medium text-gray-900">
                                                {t.data.meta.name}
                                            </span>
                                            <Show when={(t.data as any).agents?.length}>
                                                <span class="ml-2 text-xs text-gray-400">
                                                    {(t.data as any).agents.length} aktörer
                                                </span>
                                            </Show>
                                        </div>
                                        <Show when={idx === EMPTY_TEMPLATE_IDX}>
                                            <span class="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                                                Redigera
                                            </span>
                                        </Show>
                                    </button>
                                )
                            })}
                        </div>

                        {/* Redigerbar kopia checkbox (hidden for Tomt scenario) */}
                        <Show when={selectedTemplateIdx() !== null && !isEmptyTemplate()}>
                            <label class="mb-4 flex cursor-pointer items-center gap-2 px-1">
                                <input
                                    type="checkbox"
                                    checked={editable()}
                                    onChange={(e) => setEditable(e.currentTarget.checked)}
                                    class="h-4 w-4 accent-blue-600"
                                />
                                <span class="text-sm text-gray-700">Redigerbar kopia</span>
                                <span class="text-xs text-gray-400">— spara och ändra aktörer</span>
                            </label>
                        </Show>

                        {/* Editable detail form (template) */}
                        <Show when={(editable() || isEmptyTemplate()) && selectedTemplateIdx() !== null}>
                            <div class="mb-5 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div>
                                    <label class="mb-1 block text-xs font-medium text-gray-600">
                                        Scenarionamn (max 16 tecken)
                                    </label>
                                    <input
                                        type="text"
                                        value={scenarioName()}
                                        onInput={(e) => setScenarioName(e.currentTarget.value.slice(0, 16))}
                                        placeholder={TEMPLATES[selectedTemplateIdx()!]?.data.meta.name}
                                        maxLength={16}
                                        class="w-full rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div class="flex gap-4">
                                    <div class="flex-1">
                                        <label class="mb-1 block text-xs font-medium text-gray-600">Starttid</label>
                                        <div class="flex items-center gap-1">
                                            <input type="number" min="0" max="23" value={startHour()} onInput={(e) => setStartHour(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                            <span class="text-gray-500">:</span>
                                            <input type="number" min="0" max="59" value={startMinute()} onInput={(e) => setStartMinute(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                        </div>
                                    </div>
                                    <div class="flex-1">
                                        <label class="mb-1 block text-xs font-medium text-gray-600">Brottstid</label>
                                        <div class="flex items-center gap-1">
                                            <input type="number" min="0" max="23" value={crimeHour()} onInput={(e) => setCrimeHour(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                            <span class="text-gray-500">:</span>
                                            <input type="number" min="0" max="59" value={crimeMinute()} onInput={(e) => setCrimeMinute(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                        </div>
                                    </div>
                                </div>
                                <label class="flex cursor-pointer items-center gap-2">
                                    <input type="checkbox" checked={soundEffectsEnabled()} onChange={(e) => setSoundEffectsEnabled(e.currentTarget.checked)} class="h-4 w-4 accent-blue-600" />
                                    <span class="text-sm text-gray-700">Ljudeffekter</span>
                                </label>
                            </div>
                        </Show>

                        {/* Secondary options */}
                        <div class="mb-5 border-t border-gray-100 pt-4">
                            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Eller</p>
                            <div class="space-y-2">
                                <label class={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 hover:bg-gray-50 ${secondaryType() === "clone-current" ? "border-blue-400 bg-blue-50" : "border-gray-200"}`}>
                                    <input type="radio" name="secondary" checked={secondaryType() === "clone-current"} onChange={() => selectSecondary("clone-current")} class="mt-0.5 h-4 w-4 accent-blue-600" />
                                    <div>
                                        <div class="text-sm font-medium text-gray-900">Utgå från aktuellt</div>
                                        <div class="text-xs text-gray-500">Redigerbar kopia av nuvarande scenario</div>
                                    </div>
                                </label>
                                <label class={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 hover:bg-gray-50 ${secondaryType() === "from-file" ? "border-blue-400 bg-blue-50" : "border-gray-200"}`}>
                                    <input type="radio" name="secondary" checked={secondaryType() === "from-file"} onChange={() => selectSecondary("from-file")} class="mt-0.5 h-4 w-4 accent-blue-600" />
                                    <div>
                                        <div class="text-sm font-medium text-gray-900">Ladda från fil</div>
                                        <div class="text-xs text-gray-500">Importera scenario från JSON-fil</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Clone-current detail form */}
                        <Show when={secondaryType() === "clone-current"}>
                            <div class="mb-5 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <div>
                                    <label class="mb-1 block text-xs font-medium text-gray-600">Scenarionamn (max 16 tecken)</label>
                                    <input type="text" value={scenarioName()} onInput={(e) => setScenarioName(e.currentTarget.value.slice(0, 16))} placeholder="Mitt scenario" maxLength={16} class="w-full rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                                </div>
                                <div class="flex gap-4">
                                    <div class="flex-1">
                                        <label class="mb-1 block text-xs font-medium text-gray-600">Starttid</label>
                                        <div class="flex items-center gap-1">
                                            <input type="number" min="0" max="23" value={startHour()} onInput={(e) => setStartHour(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                            <span class="text-gray-500">:</span>
                                            <input type="number" min="0" max="59" value={startMinute()} onInput={(e) => setStartMinute(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                        </div>
                                    </div>
                                    <div class="flex-1">
                                        <label class="mb-1 block text-xs font-medium text-gray-600">Brottstid</label>
                                        <div class="flex items-center gap-1">
                                            <input type="number" min="0" max="23" value={crimeHour()} onInput={(e) => setCrimeHour(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                            <span class="text-gray-500">:</span>
                                            <input type="number" min="0" max="59" value={crimeMinute()} onInput={(e) => setCrimeMinute(parseInt(e.currentTarget.value) || 0)} class="w-12 rounded border border-gray-300 bg-white px-1.5 py-1 text-center text-sm text-gray-900" />
                                        </div>
                                    </div>
                                </div>
                                <label class="flex cursor-pointer items-center gap-2">
                                    <input type="checkbox" checked={soundEffectsEnabled()} onChange={(e) => setSoundEffectsEnabled(e.currentTarget.checked)} class="h-4 w-4 accent-blue-600" />
                                    <span class="text-sm text-gray-700">Ljudeffekter</span>
                                </label>
                            </div>
                        </Show>

                        {/* File upload section */}
                        <Show when={secondaryType() === "from-file"}>
                            <div class="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <label class="mb-2 block text-sm font-medium text-gray-700">Välj JSON-fil</label>
                                <input type="file" accept=".json" onChange={handleFileUpload} class="block w-full cursor-pointer rounded border border-gray-300 bg-white text-sm text-gray-900 file:mr-4 file:rounded file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100" />
                                <Show when={fileError()}>
                                    <p class="mt-2 text-sm text-red-600">{fileError()}</p>
                                </Show>
                                <Show when={fileData() && !fileError()}>
                                    <p class="mt-2 text-sm text-green-600">✓ Fil laddad: {scenarioName()}</p>
                                </Show>
                            </div>
                        </Show>

                        {/* Action buttons */}
                        <div class="flex justify-end gap-3">
                            <button onClick={props.onClose} class="rounded bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200">
                                Avbryt
                            </button>
                            <button onClick={handleConfirm} disabled={isConfirmDisabled()} class="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                                {confirmLabel()}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Show>
    )
}

export default ScenarioSelectionModal
